import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, Inject, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { WINDOW } from '@core/injectors';
import { ConfigService, UserService } from '@core/services';
import { CustomSortingTableDataSource, initializeSearchFromParams, sortingColumns } from '@core/utils';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  Subscription,
  throwError,
} from 'rxjs';
import { OpenTicketsDTO, OpenTicketsItem } from '../../models';
import { IssuesDashboardDataService } from '../../services/issues-dashboard-data/issues-dashboard-data.service';

@Component({
  selector: 'app-open-tickets-table',
  templateUrl: './open-tickets-table.component.html',
  styleUrl: './open-tickets-table.component.scss',
  standalone: false,
})
export class OpenTicketsTableComponent {
  dataList$!: Observable<any>;

  isLoading = signal<boolean>(false);
  resultsLength = signal<number>(0);
  currentPage = signal<number>(0);
  pageSize = signal<number>(0);
  ordering = signal<string[]>([]);
  emptyListMessage = signal<string>('No results found');

  zendeskAgent: boolean | undefined;
  private winRef!: WindowProxy | null;

  formSearch = new FormControl('');
  filterCustomerName = new FormControl('');
  filterZendeskId = new FormControl('');
  filterSubject = new FormControl('');
  filterStatus = new FormControl('');

  displayedColumns = ['customer_name', 'zendesk_id', 'subject', 'status', 'updated_at'];
  filterColumns = [
    'filter_customer_name',
    'filter_zendesk_id',
    'filter_subject',
    'filter_status',
    'filter_updated_at',
  ];
  ALLOWED_SORT_COLUMNS = new Set(['zendesk_id', 'status', 'updated_at']);

  readonly ticketStatusOptions = ['new', 'open', 'pending', 'hold'];

  private sortSubscription!: Subscription;
  private filterSubscriptions: Subscription[] = [];
  private readonly relevantParamKeys = [
    'ot_page',
    'page_size',
    'ot_search',
    'ot_customer_name',
    'ot_zendesk_id',
    'ot_subject',
    'ot_status',
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    @Inject(WINDOW) private readonly win: Window,
    private IssuesDashboardDataService: IssuesDashboardDataService,
    private readonly userService: UserService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef,
    private readonly configService: ConfigService,
  ) {
    this.userService
      .getUserData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userData) => {
        this.zendeskAgent = userData?.zendesk_agent;
      });
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(distinctUntilChanged((prev, curr) => this.relevantParamKeys.every((key) => prev[key] === curr[key])))
      .subscribe((params) => {
        this.currentPage.set(+params['ot_page'] || 1);
        this.pageSize.set(+params['page_size'] || 20);
        initializeSearchFromParams(this.formSearch, params['ot_search'] || '');
        initializeSearchFromParams(this.filterCustomerName, params['ot_customer_name'] || '');
        initializeSearchFromParams(this.filterZendeskId, params['ot_zendesk_id'] || '');
        initializeSearchFromParams(this.filterSubject, params['ot_subject'] || '');
        initializeSearchFromParams(this.filterStatus, params['ot_status'] || '');
        this.updateList();
      });
  }

  ngAfterViewInit() {
    this.sortSubscription = sortingColumns(this.sort, this.currentPage, this.ordering, this.ALLOWED_SORT_COLUMNS, () =>
      this.updateList(),
    );
    this.filterSubscriptions = [
      this.setupFilterParam(this.formSearch, 'ot_search'),
      this.setupFilterParam(this.filterCustomerName, 'ot_customer_name'),
      this.setupFilterParam(this.filterZendeskId, 'ot_zendesk_id'),
      this.setupFilterParam(this.filterSubject, 'ot_subject'),
      this.setupFilterParam(this.filterStatus, 'ot_status'),
    ];
  }

  private setupFilterParam(control: FormControl, paramKey: string): Subscription {
    return control.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value) => {
      // The zendesk_id filter uses <input type="number">, so its FormControl value is a number
      // (or null), not a string. Coerce to a string before trimming — calling .trim() directly
      // on a number throws and would silently kill the navigation for that filter.
      const normalizedValue = value == null ? '' : String(value).trim();
      this.router.navigate([], {
        queryParams: { [paramKey]: normalizedValue, ot_page: 1 },
        queryParamsHandling: 'merge',
      });
    });
  }

  private openTab(key: string, url: string) {
    if (!this.winRef || this.winRef.closed) {
      this.winRef = this.win.open(url, '_blank');
      this.winRef?.focus();
    } else {
      this.winRef.location.href = url;
      this.winRef.focus();
    }
  }

  seeMore() {
    const url = this.configService.zendeskUrl + (this.zendeskAgent ? '/agent/' : '/hc/en-us/requests/organization');
    this.openTab('all', url);
  }

  navigateToItem(url: string) {
    this.openTab('all', url);
  }

  private updateList(): void {
    const params = this.route.snapshot.queryParams;
    this.isLoading.set(true);
    this.dataList$ = this.IssuesDashboardDataService.getTickets(
      this.currentPage(),
      this.pageSize(),
      this.ordering().join(','),
      params['ot_search'] || '',
      params['ot_status'] || '',
      params['ot_zendesk_id'] || '',
      params['ot_subject'] || '',
      params['ot_customer_name'] || '',
    ).pipe(
      map((data: OpenTicketsDTO) => {
        this.resultsLength.set(data.count);
        this.isLoading.set(false);
        return new CustomSortingTableDataSource<OpenTicketsItem>(data.results);
      }),
      catchError((error: HttpErrorResponse) => {
        this.isLoading.set(false);
        if (error.status >= 400 && error.status <= 500) {
          this.emptyListMessage.set('Unable to display data. Please try again later.');
          return of(new CustomSortingTableDataSource<OpenTicketsItem>([]));
        } else {
          console.error('An error occurred:', error);
          return throwError(() => error);
        }
      }),
    );
  }

  onPageChange(event: any) {
    const { pageIndex, pageSize } = event;
    this.currentPage.set(pageIndex + 1);
    this.pageSize.set(pageSize);
    this.updateList();
  }

  ngOnDestroy(): void {
    this.sortSubscription?.unsubscribe();
    this.filterSubscriptions.forEach((sub) => sub.unsubscribe());
  }
}
